import { useQueries, useQuery } from '@tanstack/react-query';
import { useGT } from 'gt-react';
import { Action, Subject } from '@erp/authz';
import type { WorkbookCatalogEntry, WorkbookSource } from '@erp/shared-types';
import { orpc } from '@/api/rpc/client';
import { useCan } from '@/hooks/useCan';
import {
  schemaGroupReading,
  type SchemaGroupReading,
} from './workbookSchemaGroup';
import { workbookSourceLabeler } from './workbookSourceLabels';

type SchemaGroupSource = Extract<WorkbookSource, { kind: 'schema-group' }>;

/**
 * What each group of `sources` reads, from its schema's complete membership (the
 * catalog is capped, so it can't say). The members fill in as they load; a
 * schema's name comes from the groupable schemas, else the catalog.
 */
export function useSchemaGroupReadings(
  sources: readonly WorkbookSource[],
  catalog: readonly WorkbookCatalogEntry[],
): (source: SchemaGroupSource) => SchemaGroupReading {
  const gt = useGT();
  // Group members are Data Sources; without that right nothing is requested and
  // the name falls back to the catalog's.
  const [canReadSources] = useCan(Action.Read, Subject.DataSource);
  const schemaIds = [
    ...new Set(
      sources.flatMap((source) =>
        source.kind === 'schema-group' ? [source.schemaId] : [],
      ),
    ),
  ];
  const schemas = useQuery({
    ...orpc.workbooks.groupableSchemas.queryOptions({ input: {} }),
    enabled: canReadSources && schemaIds.length > 0,
  });
  const memberships = useQueries({
    queries: schemaIds.map((schemaId) => ({
      ...orpc.workbooks.schemaGroupMembers.queryOptions({
        input: { schemaId },
      }),
      enabled: canReadSources,
    })),
  });
  const unknownName = gt('Unavailable data source', {
    context: 'workbook schema group member',
  });
  return (source) => {
    const schemaName =
      schemas.data?.find((schema) => schema.id === source.schemaId)?.name ??
      catalog.find(
        (entry) =>
          entry.kind === 'data-source' && entry.schemaId === source.schemaId,
      )?.schemaName;
    const links = canReadSources
      ? memberships[schemaIds.indexOf(source.schemaId)]?.data
      : undefined;
    // Members read as the catalog labels them, not as their `import.…` tables.
    const label = workbookSourceLabeler(catalog, schemaName);
    return schemaGroupReading(
      source,
      {
        schemaName,
        links: links?.map((link) => ({ id: link.id, name: label(link) })),
      },
      unknownName,
    );
  };
}
